import { RECORDING_STALL_AFTER_MS } from './recordingProgress';
import type { LocalRecordingUpload } from './types';

/**
 * What is still in flight for this person, from the server's point of view
 * (TOADO-306).
 *
 * The recorder page already showed background uploads, but those live in this
 * browser's IndexedDB. A walkthrough left processing on a different machine,
 * or one whose local record was cleared, had no trace anywhere in the product
 * — which is the shape of TOADO-277, where someone finished a walkthrough,
 * navigated away, and could not find out whether it was still working or dead.
 *
 * Server session status is therefore the source of truth here, and the local
 * upload record is only used to sharpen the picture when it happens to exist.
 */

export type InFlightStage = 'recording' | 'uploading' | 'processing' | 'ready' | 'failed';

/** The subset of a session row this view needs. */
export interface RecordingSessionSummary {
  id: string;
  projectId: string;
  status: string;
  masterUploadStatus?: 'pending' | 'uploading' | 'ready' | 'failed';
  masterUploadOffset?: number;
  masterUploadTotalBytes?: number;
  /**
   * When the worker's claim on this session expires. The worker heartbeats
   * this while it works and clears it when it settles, which is what makes it
   * a liveness signal rather than a one-shot claim.
   */
  processingLeaseExpiresAt?: string;
  updatedAt?: string;
  /**
   * True when a worker is running this session right now, from the writer
   * lease rather than from `processingLeaseExpiresAt`. The retry route pushes
   * that lease thirty minutes out itself, immediately after enqueueing, so it
   * says nothing about whether anything has picked the job up.
   */
  workerActive?: boolean;
  /**
   * Where this row came from. A row synthesized from a local upload record has
   * no lease field because the browser never had one — which is not the same
   * statement as a server row that reports no lease, and must not be read as
   * one. Defaults to `'server'`.
   */
  origin?: 'server' | 'local';
}

export interface InFlightSession {
  recordingId: string;
  projectId: string;
  stage: InFlightStage;
  title: string;
  detail: string;
  /** 0-100 while bytes are moving; undefined when no fraction is meaningful. */
  percent?: number;
  /** True when waiting is no longer reasonable advice. */
  stalled: boolean;
  /** True when there is something the person can do about this one now. */
  actionable: boolean;
  /** True when this browser still holds the bytes, so retry and discard work. */
  local: boolean;
  updatedAt?: string;
}

export interface InFlightSummary {
  sessions: InFlightSession[];
  /**
   * Sessions holding a slot against the per-user cap, as counted by the server.
   * This can exceed `sessions.length`: the cap counts every session the person
   * created, while the list only shows the ones in projects they can still see.
   */
  activeCount: number;
  activeLimit: number;
  /** Slots held by sessions this list cannot show. */
  hiddenSlots: number;
  /** True when the next attempt to start a walkthrough will be refused. */
  atLimit: boolean;
  stalled: number;
  /**
   * True when checking has been failing and the last successful response is
   * old enough that nothing here should be read as current.
   *
   * Capping the projected clock stops a stale snapshot manufacturing a verdict
   * on its own, but it does not stop the snapshot's own contents being shown as
   * if they were live: a session whose worker died after the last response
   * still reads "cutting your ticket clips, this continues on the server". The
   * page needs to be able to say it could not check.
   */
  stale: boolean;
  /**
   * False when the server has not answered yet, so `activeCount` is a local
   * guess. The page hides the slot copy rather than asserting a number the
   * server never sent.
   */
  activeCountKnown: boolean;
}

/** Statuses that are done with, so they are not listed and hold no slot. */
const SETTLED = new Set(['committed', 'discarded']);

/**
 * Read a local upload record as if it were a session row.
 *
 * Used for uploads this browser is holding that the server list does not
 * mention: a session created before a project became invisible, or one whose
 * server row this account can no longer read. Dropping those would take the
 * retry and discard controls away from the only browser that has the bytes.
 */
export function sessionFromLocalUpload(upload: LocalRecordingUpload): RecordingSessionSummary {
  const status = upload.status === 'ready'
    ? 'ready_for_review'
    : upload.status === 'processing' || upload.status === 'cleanup'
      ? 'processing'
      : 'recording';
  return {
    id: upload.recordingId,
    projectId: '',
    status,
    ...(upload.status === 'failed'
      ? { masterUploadStatus: 'failed' as const }
      : upload.status === 'uploading' || upload.status === 'pending'
        ? { masterUploadStatus: 'uploading' as const }
        : {}),
    masterUploadOffset: upload.offset,
    masterUploadTotalBytes: upload.totalBytes,
    updatedAt: upload.updatedAt,
    origin: 'local',
  };
}

function percentOf(offset?: number, total?: number): number | undefined {
  if (!Number.isFinite(total) || (total ?? 0) <= 0) return undefined;
  if (!Number.isFinite(offset)) return undefined;
  return Math.min(100, Math.max(0, Math.round(((offset ?? 0) / (total as number)) * 100)));
}

function elapsedMs(now: Date, iso: string | undefined): number | undefined {
  if (!iso) return undefined;
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return undefined;
  return now.getTime() - at;
}

/**
 * How long a session may go with nothing working on it before it is called out.
 *
 * Ten renewal intervals, so a worker briefly between heartbeats is never
 * mistaken for one that has gone. It is not a claim that the session is dead:
 * processing is serialized, so a queue wait is unbounded and looks the same
 * from here. See the copy this produces.
 */
const LEASE_GRACE_MS = 10 * 60 * 1_000;

/**
 * Is anything working on this session, and if not, for how long?
 *
 * The only trustworthy "is a worker on it" signal is `workerActive`, taken
 * from the writer lease the worker holds and heartbeats. Deriving it from
 * `processingLeaseExpiresAt` does not work and the attempt is worth recording:
 * that lease looks like a worker's renewal even when no worker exists, because
 * the retry route pushes it thirty minutes out itself immediately after
 * enqueueing (`extendProcessingLease`). Every enqueued job therefore carries a
 * thirty-minute claim from the moment it is queued.
 *
 * Before processing starts there is no lease at all, so silence on
 * `updated_at` is all there is — and there it is the right signal, because a
 * session left `recording` with nothing uploading really has been abandoned.
 */
function isSessionStalled(
  session: RecordingSessionSummary,
  stage: InFlightStage,
  now: Date,
  stallAfterMs: number,
): boolean {
  // At rest by design. Silence here means nothing.
  if (stage === 'ready' || stage === 'failed') return false;

  // Only a server row can speak to any of this. A row synthesized from a local
  // upload has no lease because this browser never had one, which is not the
  // same statement as a server row reporting none.
  if (session.status === 'processing' && (session.origin ?? 'server') === 'server') {
    // A worker is on it right now. Whatever the lease says, this is working.
    if (session.workerActive) return false;
    const leaseExpiresIn = session.processingLeaseExpiresAt
      ? -(elapsedMs(now, session.processingLeaseExpiresAt) ?? 0)
      : undefined;
    if (leaseExpiresIn === undefined) return true;
    return leaseExpiresIn < -LEASE_GRACE_MS;
  }

  const idleFor = elapsedMs(now, session.updatedAt);
  return idleFor !== undefined && idleFor >= stallAfterMs;
}

function stageOf(session: RecordingSessionSummary): InFlightStage {
  if (session.masterUploadStatus === 'failed') return 'failed';
  if (session.status === 'ready_for_review') return 'ready';
  if (session.status === 'processing') return 'processing';
  // Still `recording`: either genuinely live in another tab, or an upload that
  // never reached its final byte.
  if (session.masterUploadStatus === 'uploading' || (session.masterUploadOffset ?? 0) > 0) {
    return 'uploading';
  }
  return 'recording';
}

/**
 * Fold one session into the line the page shows for it.
 *
 * `upload` is the matching local record when this browser is the one doing the
 * uploading. It only refines the percentage; it never overrides the server's
 * view of what stage the session is at, because the whole point of this list is
 * to be right about sessions this browser knows nothing about.
 */
export function describeInFlightSession(
  session: RecordingSessionSummary,
  upload: LocalRecordingUpload | undefined,
  now: Date = new Date(),
  stallAfterMs: number = RECORDING_STALL_AFTER_MS,
  /**
   * True when this observation is too old to draw conclusions from. Taken
   * here rather than patched onto the result afterwards: clearing a `stalled`
   * flag while leaving the title and instructions the stalled branch produced
   * left cards reading "Waiting longer than expected, open it to retry"
   * directly under a notice saying nothing was being checked.
   */
  stale = false,
): InFlightSession {
  const stage = stageOf(session);
  const percent = percentOf(session.masterUploadOffset, session.masterUploadTotalBytes)
    ?? percentOf(upload?.offset, upload?.totalBytes);
  const stalled = !stale && isSessionStalled(session, stage, now, stallAfterMs);

  const base = {
    recordingId: session.id,
    projectId: session.projectId,
    stage,
    stalled,
    local: upload !== undefined,
    ...(percent === undefined ? {} : { percent }),
    ...(session.updatedAt ? { updatedAt: session.updatedAt } : {}),
  };

  if (stalled) {
    // Two different situations reach this point and they need different
    // answers, because their recovery paths do not overlap.
    if (session.status === 'processing') {
      const minutes = Math.floor(LEASE_GRACE_MS / 60_000);
      return {
        ...base,
        // No worker holds this and its claim lapsed a long time ago. Whether
        // it is queued behind a long encode or genuinely abandoned is not
        // knowable from here — processing is serialized, so the queue is
        // unbounded — so the copy says both and recommends the action that
        // costs nothing when the diagnosis is wrong.
        title: 'Waiting longer than expected',
        detail: `Nothing has been working on this for over ${minutes} minutes. It may be queued behind other walkthroughs, or it may need a retry. Opening it to retry is safe either way.`,
        actionable: true,
      };
    }
    // Never reached processing: the video never finished arriving. No server
    // job exists to retry, and the clip-retry endpoint refuses an incomplete
    // master outright. The bytes are on whichever browser recorded it.
    const minutes = Math.floor(stallAfterMs / 60_000);
    return {
      ...base,
      title: 'Upload never finished',
      // A local record is not proof the bytes are still here: cleanup deletes
      // the chunks and keeps the record. `failed` is the state that actually
      // has something to resend, and it is what the Retry control keys off.
      detail: upload?.status === 'failed'
        ? `Nothing has been sent for over ${minutes} minutes. This browser still holds the video: retry the upload, or discard it if you no longer need it.`
        : `Nothing has been sent for over ${minutes} minutes. The video is on the browser that recorded it, and only that browser can finish sending it.`,
      // Only actionable where a retry exists. Elsewhere this is information.
      actionable: upload?.status === 'failed',
    };
  }

  switch (stage) {
    case 'failed':
      return {
        ...base,
        title: 'Upload needs a retry',
        detail: 'The recording is still held. Retry it from the browser that recorded it.',
        actionable: true,
      };
    case 'ready':
      return {
        ...base,
        title: 'Ready for ticket review',
        detail: 'Clips and screenshots are cut. Open it to file its tickets.',
        actionable: true,
      };
    case 'processing': {
      // A queued job is not the same as one being worked on. Saying "cutting
      // your clips" about something that has not started is the kind of small
      // untruth that costs the whole surface its credibility once someone
      // notices, and with serialized processing the wait can be long and
      // entirely normal.
      return {
        ...base,
        title: session.workerActive ? 'Cutting ticket clips' : 'Waiting to start',
        detail: session.workerActive
          ? 'This continues on the server whether or not this page is open.'
          : 'It has not started yet. This can take a while when other walkthroughs are ahead of it.',
        actionable: false,
      };
    }
    case 'uploading':
      return {
        ...base,
        title: 'Uploading walkthrough',
        detail: 'Bytes are still moving. This continues in the background.',
        actionable: false,
      };
    case 'recording':
    default:
      return {
        ...base,
        title: 'Recording still open',
        detail: 'This walkthrough has not been stopped yet. It holds a session slot until it is committed or discarded.',
        actionable: false,
      };
  }
}

/**
 * Everything not yet committed or discarded, plus what the per-user cap makes
 * of it.
 *
 * `activeCount` comes from the server rather than being counted here on
 * purpose: the cap is enforced over every session the person created, while
 * this list is filtered to projects they can still see. When those two numbers
 * disagree, a start can be refused with nothing on screen to explain it, so the
 * difference is surfaced as `hiddenSlots` rather than quietly dropped.
 */
export function summarizeInFlight(
  sessions: RecordingSessionSummary[],
  uploads: LocalRecordingUpload[],
  options: {
    activeCount?: number;
    activeLimit: number;
    now?: Date;
    stallAfterMs?: number;
    /**
     * True when checks are failing and the last response is too old to treat
     * as current. Suppresses stall verdicts entirely: a verdict about whether
     * a worker is running is worthless from a client that cannot reach the
     * server, and issuing one anyway is how a healthy job gets discarded.
     */
    stale?: boolean;
    /**
     * Sessions to leave out of the cards. The walkthrough being recorded right
     * now is the case: it has its own panel on the page, so a card for it would
     * read as a second, separate walkthrough.
     *
     * `holdsSlot` says whether the caller knows it still occupies a slot. True
     * for a recording in progress, which genuinely does even when its project
     * has just become invisible. False when the caller cannot tell — a
     * completed session may already be committed, and counting it would
     * silently absorb an unrelated slot held by something the list cannot show,
     * suppressing the very explanation that slot exists to give.
     */
    hide?: ReadonlyArray<{ id: string; holdsSlot: boolean }>;
  },
): InFlightSummary {
  const now = options.now ?? new Date();
  const uploadById = new Map(uploads.map((upload) => [upload.recordingId, upload]));
  const hidden = new Set((options.hide ?? []).map((entry) => entry.id));
  const hiddenHoldingSlot = new Set(
    (options.hide ?? []).filter((entry) => entry.holdsSlot).map((entry) => entry.id),
  );
  const live = sessions.filter((session) => !SETTLED.has(session.status));
  const shown = live.filter((session) => !hidden.has(session.id));

  const stale = options.stale === true;
  // Staleness goes in, rather than being subtracted from the result. A verdict
  // removed afterwards leaves its wording behind, which reads worse than the
  // verdict did.
  const describe = (session: RecordingSessionSummary) => describeInFlightSession(
    session,
    uploadById.get(session.id),
    now,
    options.stallAfterMs,
    stale,
  );

  const described = shown.map(describe);

  // Local-only uploads the server list did not mention. They still hold bytes
  // on this machine, so they still need somewhere to be retried or discarded.
  //
  // Keyed off every session the server sent, shown or not. A walkthrough
  // committed or discarded from its review page is not among them (the list
  // is active sessions only): what keeps it from coming back here as an
  // unfinished "ready for review" card is that its page released this
  // browser's copy (`releaseLocalRecording`).
  const listed = new Set(sessions.map((session) => session.id));
  for (const upload of uploads) {
    if (listed.has(upload.recordingId) || hidden.has(upload.recordingId)) continue;
    described.push(describe(sessionFromLocalUpload(upload)));
  }

  // Anything asking for a decision sorts above anything that is merely working,
  // so a stuck walkthrough is never pushed below five healthy ones.
  const weight = (item: InFlightSession) => {
    if (item.stalled || item.stage === 'failed') return 0;
    return item.stage === 'ready' ? 1 : 2;
  };
  described.sort((a, b) => {
    const byWeight = weight(a) - weight(b);
    if (byWeight !== 0) return byWeight;
    const at = Date.parse(a.updatedAt ?? '');
    const bt = Date.parse(b.updatedAt ?? '');
    if (!Number.isFinite(at) || !Number.isFinite(bt)) return 0;
    return bt - at;
  });

  const activeCountKnown = Number.isFinite(options.activeCount);
  const activeCount = activeCountKnown ? (options.activeCount as number) : described.length;

  // Slots we can account for: every live session the server named, plus any
  // hidden id the caller vouched for as still holding one. The walkthrough
  // being recorded right now is that case — if its project has just been
  // archived it drops out of `items`, and without this the page would report a
  // slot held by an inaccessible walkthrough while pointing at the panel
  // recording that very walkthrough. A hidden id the caller could not vouch
  // for is deliberately not counted: it may already be committed, and
  // absorbing a real hidden slot into it would suppress the explanation.
  const accountedFor = live.length
    + [...hiddenHoldingSlot].filter((id) => !live.some((session) => session.id === id)).length;

  return {
    sessions: described,
    activeCount,
    activeLimit: options.activeLimit,
    // A local-only entry is not evidence that a slot is free: its server row
    // may be exactly one of the ones this account can no longer read.
    hiddenSlots: activeCountKnown ? Math.max(0, activeCount - accountedFor) : 0,
    atLimit: activeCountKnown && activeCount >= options.activeLimit,
    stalled: described.filter((item) => item.stalled).length,
    activeCountKnown,
    stale,
  };
}
